import { useEffect, useMemo, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { ArrowUpRight, Briefcase, CheckCircle2, Clock3, Inbox, LogOut, Search, Send, Sparkles } from "lucide-react";
import { JobCard } from "./components/JobCard";
import { Sidebar, type View } from "./components/Sidebar";
import { isSupabaseConfigured, supabase } from "./services/supabase";
import type { EmailEvent, Job } from "./types";
import "./styles.css";

export default function App({ session }: { session: Session }) {
  const [view, setView] = useState<View>("pending");
  const [jobs, setJobs] = useState<Job[]>([]);
  const [events, setEvents] = useState<EmailEvent[]>([]);
  const [loadError, setLoadError] = useState("");
  const [query, setQuery] = useState("");
  const [source, setSource] = useState("Todos");

  useEffect(() => {
    if (!supabase) return;
    void Promise.all([
      supabase.from("jobs").select("*").order("discovered_at", { ascending: false }),
      supabase.from("email_events").select("*").order("received_at", { ascending: false }),
    ]).then(([jobResult, eventResult]) => {
      const error = jobResult.error ?? eventResult.error;
      if (error) {
        setLoadError(error.message);
        return;
      }
      setJobs((jobResult.data ?? []).map((row) => ({
        id: row.id,
        title: row.title,
        company: row.company,
        source: row.source,
        location: row.location ?? "No especificada",
        modality: row.modality ?? "No especificada",
        salary: row.salary_text ?? "No especificado",
        score: row.score ?? 0,
        matchingSkills: row.matching_skills ?? [],
        missingSkills: row.missing_skills ?? [],
        recommendedCv: row.recommended_cv ?? "CV_Douglas_Guerrero_V2.docx",
        status: row.status,
        discoveredAt: new Date(row.discovered_at).toLocaleString("es-CO"),
        url: row.url,
      })));
      setEvents((eventResult.data ?? []).map((row) => ({
        id: row.id,
        company: row.company ?? "Sin identificar",
        title: row.position ?? row.subject ?? "Correo",
        classification: row.classification,
        snippet: row.snippet ?? "",
        confidence: Number(row.confidence),
        receivedAt: row.received_at ? new Date(row.received_at).toLocaleString("es-CO") : "Sin fecha",
      })));
    });
  }, [session.user.id]);

  const visibleJobs = useMemo(() => jobs.filter((job) => {
    const pending = view !== "applications" ? job.status !== "APPLIED_CONFIRMED" : job.status === "APPLIED_CONFIRMED";
    const matchesSource = source === "Todos" || job.source === source;
    const haystack = `${job.title} ${job.company} ${job.matchingSkills.join(" ")}`.toLowerCase();
    return pending && matchesSource && haystack.includes(query.toLowerCase());
  }), [jobs, query, source, view]);

  const updateStatus = async (id: string, status: Job["status"]) => {
    const previous = jobs;
    setJobs((current) => current.map((job) => job.id === id ? { ...job, status } : job));
    const { error } = await supabase!.from("jobs").update({ status, updated_at: new Date().toISOString() }).eq("id", id);
    if (error) {
      setJobs(previous);
      setLoadError(error.message);
    }
  };

  return (
    <div className="app-shell">
      <Sidebar view={view} onChange={setView} />
      <main>
        <header className="topbar">
          <div><p className="kicker">VIERNES, 28 DE AGOSTO</p><h1>{view === "pending" ? "Buenos días, Douglas" : view === "applications" ? "Postulaciones realizadas" : view === "responses" ? "Respuestas importantes" : "Configuración"}</h1></div>
          <div className="top-actions"><span className={isSupabaseConfigured ? "mode live" : "mode"}>{isSupabaseConfigured ? "Supabase conectado" : "Modo demostración"}</span><button className="avatar" onClick={() => void supabase?.auth.signOut()} title="Cerrar sesión"><LogOut size={17} /></button></div>
        </header>

        {loadError && <p className="load-error">{loadError}</p>}

        {view === "responses" ? <Responses events={events} /> : view === "settings" ? <SettingsPanel /> : (
          <>
            <section className="metrics">
              <Metric icon={Sparkles} label="Pendientes" value={jobs.filter((j) => j.status === "READY").length} tone="lime" />
              <Metric icon={Clock3} label="Sin confirmar" value={jobs.filter((j) => j.status === "APPLICATION_OPENED").length} tone="amber" />
              <Metric icon={CheckCircle2} label="Aplicadas" value={jobs.filter((j) => j.status === "APPLIED_CONFIRMED").length} tone="blue" />
              <Metric icon={Inbox} label="Respuestas" value={events.length} tone="purple" />
            </section>
            <section className="goal-card">
              <div><span className="goal-icon"><Briefcase size={20} /></span><div><strong>Objetivo de hoy</strong><small>8 de 10 postulaciones priorizadas</small></div></div>
              <div className="progress"><span style={{ width: "80%" }} /></div><b>80%</b>
            </section>
            <section className="content-head">
              <div><h2>{view === "applications" ? "Historial reciente" : "Listas para aplicar"}</h2><p>Ordenadas por compatibilidad y fecha de publicación.</p></div>
              <div className="filters"><label><Search size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar cargo, empresa o skill" /></label><select value={source} onChange={(event) => setSource(event.target.value)}><option>Todos</option><option>LinkedIn</option><option>Computrabajo</option><option>Magneto</option><option>Email</option></select></div>
            </section>
            <section className="job-list">{visibleJobs.length ? visibleJobs.map((job) => <JobCard key={job.id} job={job} onStatus={updateStatus} />) : <div className="empty"><CheckCircle2 size={36} /><h3>No hay elementos en esta vista</h3><p>Los cambios de estado aparecerán aquí automáticamente.</p></div>}</section>
          </>
        )}
      </main>
    </div>
  );
}

function Metric({ icon: Icon, label, value, tone }: { icon: typeof Briefcase; label: string; value: number; tone: string }) {
  return <article className="metric"><span className={`metric-icon ${tone}`}><Icon size={20} /></span><div><strong>{value}</strong><small>{label}</small></div><ArrowUpRight size={17} /></article>;
}

function Responses({ events }: { events: EmailEvent[] }) {
  return <section className="responses"><div className="section-intro"><h2>Acciones que necesitan atención</h2><p>Clasificadas automáticamente desde Gmail; las ambiguas quedan para revisión.</p></div>{events.length ? events.map((event) => <article className="response-card" key={event.id}><span className={`response-icon ${event.classification.toLowerCase()}`}>{event.classification === "INTERVIEW" ? <Inbox /> : <Briefcase />}</span><div><span className="eyebrow">{event.classification.replaceAll("_", " ")} · {event.confidence}% confianza</span><h3>{event.company} · {event.title}</h3><p>“{event.snippet}”</p><small>{event.receivedAt}</small></div><button className="button"><ArrowUpRight size={16} /> Abrir correo</button></article>) : <div className="empty"><Inbox size={36} /><h3>No hay respuestas todavía</h3><p>Los correos clasificados aparecerán aquí.</p></div>}</section>;
}

function SettingsPanel() {
  return <section className="settings-panel"><div className="section-intro"><h2>Reglas de automatización</h2><p>Los envíos permanecen bloqueados hasta activar el modo live en el entorno seguro.</p></div><div className="settings-grid"><article><label>Puntuación mínima</label><strong>75</strong><input type="range" min="50" max="95" defaultValue="75" /></article><article><label>Envío automático</label><strong className="safe"><Send size={18} /> Dry-run</strong><p>Prepara el correo, pero no lo transmite.</p></article><article><label>Revisión de Gmail</label><strong>Cada 30 minutos</strong><p>Confirmaciones, entrevistas, pruebas y rechazos.</p></article><article><label>CV principal</label><strong>CV_Douglas_Guerrero_V2.docx</strong><p>Nombre neutral y controlado por versión.</p></article></div></section>;
}
